import React from 'react';
import { MessageCircle, Instagram, Facebook } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { whatsappNumber, socialMedia } from '../data/mock';

const Footer = () => {
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const handleWhatsAppClick = () => {
    navigate('/order');
  };

  const scrollToSection = (id) => {
    if (location.pathname !== '/') {
      navigate('/#' + id);
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-[#3E2723] relative overflow-hidden">
      {/* CTA Section */}
      <div className="relative z-10 bg-gradient-to-br from-[#B22222] to-[#8B0000] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center animate-in fade-in slide-in-from-bottom duration-700">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              {t.footer.cta.title}
            </h2>
            <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-3xl mx-auto">
              {t.footer.cta.subtitle}
            </p>
            <Button
              onClick={handleWhatsAppClick}
              size="lg"
              className="bg-white text-[#B22222] hover:bg-[#FED09D] px-8 py-6 text-lg rounded-full transition-all duration-300 hover:shadow-2xl hover:scale-105 group"
            >
              <MessageCircle className="mr-2 group-hover:scale-110 transition-transform duration-300" size={24} />
              {t.footer.cta.button}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* About */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#B22222] rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xl">GB</span>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">{t.footer.about}</h3>
              </div>
            </div>
            <p className="text-white/70 leading-relaxed mb-6 max-w-md">
              {t.footer.aboutText}
            </p>
            <div className="flex gap-4">
              <a
                href={socialMedia.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 hover:bg-[#B22222] rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                aria-label="Instagram"
              >
                <Instagram className="text-white" size={20} />
              </a>
              <a
                href={socialMedia.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 hover:bg-[#B22222] rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                aria-label="Facebook"
              >
                <Facebook className="text-white" size={20} />
              </a>
              <a
                href={socialMedia.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 hover:bg-[#B22222] rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                aria-label="TikTok"
              >
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-bold text-lg mb-4">{t.footer.quickLinks}</h4>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => navigate('/order')}
                  className="text-white/70 hover:text-[#B22222] transition-colors duration-300 font-bold"
                >
                  Menu & Pesan
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('benefits')}
                  className="text-white/70 hover:text-[#B22222] transition-colors duration-300"
                >
                  {t.nav.benefits}
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('testimonials')}
                  className="text-white/70 hover:text-[#B22222] transition-colors duration-300"
                >
                  {t.nav.testimonials}
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="text-white/70 hover:text-[#B22222] transition-colors duration-300"
                >
                  {t.nav.contact}
                </button>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-bold text-lg mb-4">{t.nav.contact}</h4>
            <ul className="space-y-3 text-white/70">
              <li>{t.contact.address}</li>
              <li>
                <a href={`tel:${t.contact.phone}`} className="hover:text-[#B22222] transition-colors duration-300">
                  {t.contact.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${t.contact.email}`} className="hover:text-[#B22222] transition-colors duration-300">
                  {t.contact.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/60 text-sm">
              {t.footer.copyright}
            </p>
            <div className="flex gap-6 text-sm text-white/60">
              <button className="hover:text-[#B22222] transition-colors duration-300">
                {language === 'id' ? 'Kebijakan Privasi' : 'Privacy Policy'}
              </button>
              <button className="hover:text-[#B22222] transition-colors duration-300">
                {language === 'id' ? 'Syarat & Ketentuan' : 'Terms & Conditions'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#B22222]/10 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2"></div>
      <div className="absolute top-0 right-0 w-64 h-64 bg-[#FED09D]/5 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2"></div>
    </footer>
  );
};

export default Footer;
